import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  CancelTokenSource,
} from "axios";

type ManagedAxiosRequestConfig = AxiosRequestConfig & {
  cancelOnRouteChange?: boolean;
  cancelPreviousRequest?: boolean;
  requestGroupKey?: string;
  __requestId?: string;
};

type ManagedFetchInit = RequestInit & {
  cancelOnRouteChange?: boolean;
  cancelPreviousRequest?: boolean;
  requestGroupKey?: string;
};

type AxiosPendingRequest = {
  cancelOnRouteChange: boolean;
  cancelSource: CancelTokenSource;
  requestKey?: string;
};

type FetchPendingRequest = {
  cancelOnRouteChange: boolean;
  controller: AbortController;
  detachSignalListener?: () => void;
  requestKey?: string;
};

const pendingAxiosRequests = new Map<string, AxiosPendingRequest>();
const pendingFetchRequests = new Map<string, FetchPendingRequest>();
const latestAxiosRequestByKey = new Map<string, string>();
const latestFetchRequestByKey = new Map<string, string>();

let axiosCancellationInitialized = false;
let fetchCancellationInitialized = false;
let requestSequence = 0;

const pendingCancelledRequest = <T>() => new Promise<T>(() => {});

const createRequestId = (prefix: string) => {
  requestSequence += 1;
  return `${prefix}-${Date.now()}-${requestSequence}`;
};

const normalizeMethod = (method?: string) => (method || "GET").toUpperCase();

const stripQueryString = (url: string) => url.split("?")[0];

const isStaticAssetUrl = (url: string) => {
  const normalizedUrl = stripQueryString(url).toLowerCase();

  const staticAssetExtensions = [
    ".svg",
    ".png",
    ".jpg",
    ".jpeg",
    ".gif",
    ".webp",
    ".ico",
    ".css",
    ".js",
    ".woff",
    ".woff2",
    ".ttf",
    ".eot",
    ".otf",
  ];

  if (staticAssetExtensions.some((ext) => normalizedUrl.endsWith(ext))) {
    return true;
  }

  return (
    normalizedUrl.includes("/static/") ||
    normalizedUrl.includes("/media/") ||
    normalizedUrl.includes("/assets/")
  );
};

const normalizeHeaderValue = (value: unknown) => {
  if (value === undefined || value === null) {
    return "";
  }
  if (Array.isArray(value)) {
    return value.join(",");
  }
  return String(value);
};

const getRequestHeaders = (config?: AxiosRequestConfig) => {
  const headers = config?.headers;
  if (!headers) {
    return {} as Record<string, unknown>;
  }
  return headers as Record<string, unknown>;
};

const buildAxiosRequestKey = (config: AxiosRequestConfig) => {
  const headers = getRequestHeaders(config);
  return [
    normalizeMethod(config.method),
    stripQueryString(config.url || ""),
    normalizeHeaderValue(headers.module),
    normalizeHeaderValue(headers.warehouse),
    normalizeHeaderValue(config.responseType),
  ].join("::");
};

const getFetchUrl = (input: RequestInfo | URL) => {
  if (typeof input === "string") {
    return input;
  }
  if (input instanceof URL) {
    return input.toString();
  }
  return input.url;
};

const normalizeFetchInput = (input: RequestInfo | URL): RequestInfo => {
  if (input instanceof URL) {
    return input.toString();
  }

  return input;
};

const getFetchMethod = (input: RequestInfo | URL, init?: RequestInit) => {
  if (init?.method) {
    return normalizeMethod(init.method);
  }
  if (typeof Request !== "undefined" && input instanceof Request) {
    return normalizeMethod(input.method);
  }
  return "GET";
};

const buildFetchRequestKey = (input: RequestInfo | URL, init?: RequestInit) => {
  return [getFetchMethod(input, init), stripQueryString(getFetchUrl(input))].join(
    "::"
  );
};

const isReadRequest = (method?: string) => {
  const normalizedMethod = normalizeMethod(method);
  return normalizedMethod === "GET" || normalizedMethod === "HEAD";
};

const shouldCancelPreviousRequest = (
  method: string | undefined,
  cancelPreviousRequest?: boolean
) => {
  if (typeof cancelPreviousRequest === "boolean") {
    return cancelPreviousRequest;
  }
  return isReadRequest(method);
};

const cleanupAxiosRequest = (config?: ManagedAxiosRequestConfig) => {
  const requestId = config?.__requestId;
  if (!requestId) {
    return;
  }

  const pendingRequest = pendingAxiosRequests.get(requestId);
  pendingAxiosRequests.delete(requestId);
  if (pendingRequest?.requestKey) {
    const activeRequestId = latestAxiosRequestByKey.get(pendingRequest.requestKey);
    if (activeRequestId === requestId) {
      latestAxiosRequestByKey.delete(pendingRequest.requestKey);
    }
  }
};

const cleanupFetchRequest = (requestId: string) => {
  const pendingRequest = pendingFetchRequests.get(requestId);
  if (!pendingRequest) {
    return;
  }

  pendingRequest.detachSignalListener?.();
  pendingFetchRequests.delete(requestId);
  if (pendingRequest.requestKey) {
    const activeRequestId = latestFetchRequestByKey.get(pendingRequest.requestKey);
    if (activeRequestId === requestId) {
      latestFetchRequestByKey.delete(pendingRequest.requestKey);
    }
  }
};

const cancelPreviousAxiosRequest = (requestKey: string, reason: string) => {
  const requestId = latestAxiosRequestByKey.get(requestKey);
  if (!requestId) {
    return;
  }

  const pendingRequest = pendingAxiosRequests.get(requestId);
  pendingRequest?.cancelSource.cancel(reason);
};

const cancelPreviousFetchRequest = (requestKey: string, reason: string) => {
  const requestId = latestFetchRequestByKey.get(requestKey);
  if (!requestId) {
    return;
  }

  const pendingRequest = pendingFetchRequests.get(requestId);
  pendingRequest?.controller.abort();
  if (pendingRequest) {
    cleanupFetchRequest(requestId);
  }
};

export const cancelAllPendingRequests = (reason = "Navigation changed") => {
  pendingAxiosRequests.forEach((pendingRequest) => {
    if (pendingRequest.cancelOnRouteChange) {
      pendingRequest.cancelSource.cancel(reason);
    }
  });

  pendingFetchRequests.forEach((pendingRequest, requestId) => {
    if (pendingRequest.cancelOnRouteChange) {
      pendingRequest.controller.abort();
      cleanupFetchRequest(requestId);
    }
  });
};

export const setupAxiosRequestCancellation = (axiosInstance: AxiosInstance) => {
  if (axiosCancellationInitialized) {
    return;
  }

  axiosCancellationInitialized = true;

  axiosInstance.interceptors.request.use((config) => {
    const managedConfig = config as ManagedAxiosRequestConfig;
    if (managedConfig.url && isStaticAssetUrl(managedConfig.url)) {
      // Ensure headers is never undefined
      if (!managedConfig.headers) {
        managedConfig.headers = {};
      }
      return managedConfig as typeof config;
    }
    const requestId = createRequestId("axios");
    const cancelSource = axios.CancelToken.source();
    const requestKey =
      managedConfig.requestGroupKey || buildAxiosRequestKey(managedConfig);

    managedConfig.__requestId = requestId;
    managedConfig.cancelToken = managedConfig.cancelToken || cancelSource.token;

    if (
      requestKey &&
      shouldCancelPreviousRequest(
        managedConfig.method,
        managedConfig.cancelPreviousRequest
      )
    ) {
      cancelPreviousAxiosRequest(requestKey, "Superseded by a newer request");
      latestAxiosRequestByKey.set(requestKey, requestId);
    }

    pendingAxiosRequests.set(requestId, {
      cancelOnRouteChange: managedConfig.cancelOnRouteChange !== false,
      cancelSource,
      requestKey,
    });

    // Ensure headers is never undefined
    if (!managedConfig.headers) {
      managedConfig.headers = {};
    }
    return managedConfig as typeof config;
  });

  axiosInstance.interceptors.response.use(
    (response) => {
      cleanupAxiosRequest(response.config as ManagedAxiosRequestConfig);
      return response;
    },
    (error: AxiosError) => {
      cleanupAxiosRequest(error.config as ManagedAxiosRequestConfig | undefined);

      if (axios.isCancel(error)) {
        // For cancelled requests, propagate a rejected promise so
        // callers' catch handlers run and UI state (e.g. loading flags)
        // can be properly cleaned up. Swallowing the cancellation with
        // a never-resolving promise causes awaiting code to hang.
        // return Promise.reject(error);

        return pendingCancelledRequest<Response>();
      }

      return Promise.reject(error);
    }
  );
};

export const setupGlobalFetchCancellation = () => {
  if (fetchCancellationInitialized || typeof window === "undefined") {
    return;
  }

  fetchCancellationInitialized = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url = getFetchUrl(input);
    if (isStaticAssetUrl(url)) {
      return originalFetch(normalizeFetchInput(input), init);
    }
    const managedInit = (init || {}) as ManagedFetchInit;
    const requestId = createRequestId("fetch");
    const requestKey =
      managedInit.requestGroupKey || buildFetchRequestKey(input, managedInit);
    const controller = new AbortController();
    const existingSignal = managedInit.signal;

    if (
      requestKey &&
      shouldCancelPreviousRequest(
        getFetchMethod(input, managedInit),
        managedInit.cancelPreviousRequest
      )
    ) {
      cancelPreviousFetchRequest(requestKey, "Superseded by a newer request");
      latestFetchRequestByKey.set(requestKey, requestId);
    }

    let detachSignalListener: (() => void) | undefined;

    if (existingSignal) {
      if (existingSignal.aborted) {
        controller.abort();
      } else {
        const handleAbort = () => controller.abort();
        existingSignal.addEventListener("abort", handleAbort, { once: true });
        detachSignalListener = () => {
          existingSignal.removeEventListener("abort", handleAbort);
        };
      }
    }

    pendingFetchRequests.set(requestId, {
      cancelOnRouteChange: managedInit.cancelOnRouteChange !== false,
      controller,
      detachSignalListener,
      requestKey,
    });

    return originalFetch(normalizeFetchInput(input), {
      ...managedInit,
      signal: controller.signal,
    })
      .then((response) => {
        cleanupFetchRequest(requestId);
        return response;
      })
      .catch((error: Error & { name?: string }) => {
        cleanupFetchRequest(requestId);

        if (error?.name === "AbortError") {
          // For aborted fetches, propagate a rejected promise instead of
          // returning a never-resolving one so callers can handle
          // cancellations and clear any loading state.
          // return Promise.reject(error);

          return pendingCancelledRequest<Response>();
        }

        return Promise.reject(error);
      });
  };
};